import { Button, Form, Input, Modal, Select, Space, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ListView from "../../components/ListView/ListView";
import CustomIcon from "../../components/CustomIcon/CustomIcon";
import MediaAssetService from "../../services/mediaAsset.service";
import {
  MEDIA_ASSET_FILE_TYPES,
  MEDIA_ASSET_PLACEHOLDER_ID,
  MEDIA_ASSET_PROPERTY_TYPES,
  getMediaAssetTaxonomy,
  isReservedMediaAssetId,
  mergeWithDefaultMediaAssetTaxonomy,
  normalizeMediaAssetType,
  setMediaAssetTaxonomy,
  toMediaAssetId,
} from "../../utils/mediaAssetsTaxonomy";
import { toastMessage } from "../../utils/utils";
import "./MediaAssets.scss";

const { Option } = Select;

const MediaAssets = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [list, setList] = useState(getMediaAssetTaxonomy());
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const fetchMediaAssets = async () => {
    setLoading(true);
    try {
      const response = await MediaAssetService.getAll();
      const taxonomy = setMediaAssetTaxonomy(response || []);
      setList(taxonomy);
    } catch (ex) {
      setList(getMediaAssetTaxonomy());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMediaAssets();
  }, []);

  const openCreateModal = () => {
    setEditingRecord(null);
    form.setFieldsValue({
      name: "",
      propertyTypes: [MEDIA_ASSET_PROPERTY_TYPES.building, MEDIA_ASSET_PROPERTY_TYPES.land],
      fileTypes: [MEDIA_ASSET_FILE_TYPES.image, MEDIA_ASSET_FILE_TYPES.pdf],
    });
    setModalVisible(true);
  };

  const openEditModal = record => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const updateLocalList = mediaAssets => {
    const taxonomy = setMediaAssetTaxonomy(mediaAssets);
    setList(taxonomy);
  };

  const saveMediaAsset = async () => {
    const isEditing = !!editingRecord;
    const values = await form.validateFields();
    const mediaAssetId = editingRecord?.id || toMediaAssetId(values.name);
    if (!editingRecord && isReservedMediaAssetId(mediaAssetId)) {
      form.setFields([
        {
          name: "name",
          errors: ["This media brochure type conflicts with a built-in media slot."],
        },
      ]);
      return;
    }

    const payload = normalizeMediaAssetType({
      ...editingRecord,
      ...values,
      id: mediaAssetId,
    });

    try {
      if (editingRecord) {
        await MediaAssetService.update(payload.id, payload);
        updateLocalList(list.map(item => (item.id === payload.id ? payload : item)));
      } else {
        await MediaAssetService.create(payload);
        updateLocalList([...list, payload]);
      }
      toastMessage("success", isEditing ? "Media Asset Type updated" : "Media Asset Type created");
      closeModal();
      fetchMediaAssets();
    } catch (ex) {
      toastMessage("error", isEditing ? "Unable to update Media Asset Type" : "Unable to create Media Asset Type");
    }
  };

  const removeMediaAsset = async record => {
    if (record.id === MEDIA_ASSET_PLACEHOLDER_ID) return;
    try {
      await MediaAssetService.remove(record.id);
      updateLocalList(list.filter(item => item.id !== record.id));
      toastMessage("success", "Media Asset Type removed");
      fetchMediaAssets();
    } catch (ex) {
      toastMessage("error", "Unable to remove Media Asset Type");
    }
  };

  const confirmRemoveMediaAsset = record => {
    Modal.confirm({
      title: t("MediaAssets.RemoveMediaBrochureType"),
      content: t("MediaAssets.RemoveConfirmation", { name: record.name }),
      okText: t("Buttons.Remove"),
      okType: "danger",
      cancelText: t("Buttons.Cancel"),
      onOk: () => removeMediaAsset(record),
    });
  };

  const getPropertyTypeLabel = type => {
    const key = Object.keys(MEDIA_ASSET_PROPERTY_TYPES).find(item => MEDIA_ASSET_PROPERTY_TYPES[item] === type);
    return t(`MediaAssets.PropertyTypes.${key || type}`, { defaultValue: type });
  };

  const getFileTypeLabel = type => {
    const key = Object.keys(MEDIA_ASSET_FILE_TYPES).find(item => MEDIA_ASSET_FILE_TYPES[item] === type);
    return t(`MediaAssets.FileTypes.${key || type}`, { defaultValue: type });
  };

  const columns = [
    {
      title: t("MediaAssets.BrochureType"),
      dataIndex: "name",
      key: "name",
      width: "30%",
      sorter: (a, b) => a.name.localeCompare(b.name),
      columnFilter: true,
      ellipsis: true,
    },
    {
      title: t("MediaAssets.PropertyType"),
      dataIndex: "propertyTypes",
      key: "propertyTypes",
      width: "25%",
      render: propertyTypes => (
        <Space size={8}>
          {(propertyTypes || []).map(type => (
            <Tag color="blue" key={type}>
              {getPropertyTypeLabel(type)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t("MediaAssets.AllowedAssetType"),
      dataIndex: "fileTypes",
      key: "fileTypes",
      width: "25%",
      render: fileTypes => (fileTypes || []).map(getFileTypeLabel).join(", "),
    },
    {
      title: t("Table.Action"),
      key: "action",
      width: "20%",
      align: "center",
      render: (_, record) =>
        record.id === MEDIA_ASSET_PLACEHOLDER_ID ? null : (
          <div className="media-assets-actions">
            <Tooltip title={t("Buttons.Edit")}>
              <Button type="link" onClick={() => openEditModal(record)}>
                <CustomIcon name="EditOutlined" />
                <span>{t("Buttons.Edit")}</span>
              </Button>
            </Tooltip>
            <Tooltip title={t("Buttons.Remove")}>
              <Button type="link" onClick={() => confirmRemoveMediaAsset(record)}>
                <CustomIcon name="DeleteOutlined" />
                <span>{t("Buttons.Remove")}</span>
              </Button>
            </Tooltip>
          </div>
        ),
    },
  ];

  return (
    <div className="media-assets-container">
      <div className="media-assets-table-shell">
        <div className="media-assets-toolbar">
          <Button type="primary" onClick={openCreateModal}>
            {t("MediaAssets.CreateMediaBrochureType")}
          </Button>
        </div>
        <ListView
          rowKey="id"
          dataSource={mergeWithDefaultMediaAssetTaxonomy(list)}
          getList={fetchMediaAssets}
          columns={columns}
          loading={loading}
          tableLayout="fixed"
          scroll={{ y: "calc(100vh - 470px)" }}
        />
      </div>
      <Modal
        visible={modalVisible}
        title={editingRecord ? t("MediaAssets.EditMediaBrochureType") : t("MediaAssets.CreateMediaBrochureType")}
        onOk={saveMediaAsset}
        onCancel={closeModal}
        okText={editingRecord ? t("Buttons.Update") : t("Buttons.Create")}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t("MediaAssets.BrochureType")}
            rules={[
              { required: true, message: "Please enter brochure type" },
              {
                validator: (_, value) => {
                  if (editingRecord || !isReservedMediaAssetId(value)) return Promise.resolve();
                  return Promise.reject(new Error("This media brochure type conflicts with a built-in media slot."));
                },
              },
            ]}
          >
            <Input disabled={editingRecord?.isSystemDefault} />
          </Form.Item>
          <Form.Item
            name="propertyTypes"
            label={t("MediaAssets.PropertyType")}
            rules={[{ required: true, message: "Please select property type" }]}
          >
            <Select mode="multiple">
              <Option value={MEDIA_ASSET_PROPERTY_TYPES.building}>{getPropertyTypeLabel("Building")}</Option>
              <Option value={MEDIA_ASSET_PROPERTY_TYPES.land}>{getPropertyTypeLabel("Land")}</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="fileTypes"
            label={t("MediaAssets.AllowedAssetType")}
            rules={[{ required: true, message: "Please select allowed asset type" }]}
          >
            <Select mode="multiple">
              <Option value={MEDIA_ASSET_FILE_TYPES.image}>{getFileTypeLabel("Image")}</Option>
              <Option value={MEDIA_ASSET_FILE_TYPES.pdf}>{getFileTypeLabel("PDF")}</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MediaAssets;
